import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import "./notificationDetail.css";
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const NotificationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [notification, setNotification] = useState(null);
    const [loading, setLoading] = useState(false);

    const clickCount = notification?.clickCount ?? notification?.clikcCount ?? notification?.click ?? 0;
    const unclickCount = notification?.unclickCount ?? notification?.unclikcCount ?? notification?.unclick ?? 0;
    const sendCount = notification?.sendCount ?? notification?.send ?? 0;
    const blockCount = notification?.blockCount ?? notification?.block ?? 0;
    const deliveryCount = notification?.deliveryCount ?? notification?.delivery ?? 0;

    const createdAtLabel = notification?.createdAt
        ? new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).format(new Date(notification.createdAt))
        : "-";

    const [copiedCode, setCopiedCode] = useState(null);

    const usableButtons = (notification?.buttons || []).filter(
        (btn) => btn?.link || btn?.discountCode || btn?.label
    );

    const copyToClipboard = async (text) => {
        if (!text) return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(text));
                return;
            }
        } catch (_) {
            // ignore and fallback
        }

        try {
            const textarea = document.createElement("textarea");
            textarea.value = String(text);
            textarea.setAttribute("readonly", "");
            textarea.style.position = "fixed";
            textarea.style.top = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        } catch (_) {
            // ignore
        }
    };

    const handleCopyCode = async (code) => {
        if (!code) return;

        await copyToClipboard(code);

        setCopiedCode(code);
        window.setTimeout(() => {
            setCopiedCode((prev) => (prev === code ? null : prev));
        }, 1500);
    };

    const StatsBar = () => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flex: "1 1 auto",
                px: "20px",
                py: "16px",
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.04)",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography
                    variant="overline"
                    sx={{ letterSpacing: 1, color: "text.secondary", lineHeight: 1 }}
                >
                    Total Customers
                </Typography>
                <Chip
                    label={notification?.customerIds?.length || 0}
                    color="primary"
                    sx={{ fontWeight: 700, height: 28 }}
                />
            </Box>
            <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
            />
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                <Box>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1, color: "#2563eb", lineHeight: 1 }}
                    >
                        Send
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                        {sendCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1, color: "#10b981", lineHeight: 1 }}
                    >
                        Deliver
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                        {deliveryCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1, color: "#16a34a", lineHeight: 1 }}
                    >
                        Click
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                        {clickCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1, color: "#f97316", lineHeight: 1 }}
                    >
                        Unclick
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                        {unclickCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1, color: "#dc2626", lineHeight: 1 }}
                    >
                        Block
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                        {blockCount}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    useEffect(() => {
        let isMounted = true;

        const fetchNotificationById = async () => {
            if (!id) return;
            setLoading(true);

            try {
                const response = await axiosInstance.get(`notificationHistory/getById/${id}`);

                if (!isMounted) return;
                if (response?.data?.isSuccess) {
                    setNotification(response?.data?.data || null);
                } else {
                    const msg = response?.data?.message || "Unable to fetch details";
                    toast.error(msg);
                }
            } catch (error) {
                const errMsg =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Something went wrong!";
                toast.error(errMsg);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchNotificationById();
        return () => {
            isMounted = false;
        };

    }, [id]);

    const thStyle = {
        textAlign: "left",
        padding: "10px",
        fontSize: 12,
        fontWeight: 600,
        color: "#6b7280",
    };

    const tdStyle = {
        padding: "10px",
        borderTop: "1px solid #f1f5f9",
        fontSize: 13,
    };

    const btnStyle = (disabled) => ({
        padding: "4px 10px",
        borderRadius: "6px",
        border: "1px solid #e5e7eb",
        background: disabled ? "#f1f5f9" : "#fff",
        color: disabled ? "#9ca3af" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "13px",
    });

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const customers = useMemo(() => notification?.customerIds || [], [notification?.customerIds]);
    const total = customers.length;
    const totalPages = Math.ceil(total / rowsPerPage);

    const paginatedCustomers = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return customers.slice(start, start + rowsPerPage);
    }, [customers, page, rowsPerPage]);

    useEffect(() => {
        setPage(1);
    }, [notification?.customerIds]);

    const getVisiblePages = () => {
        const maxVisible = 5; // how many buttons you want
        const start = Math.max(1, page - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    return (
        <div className="content-wrapper pd-top">
            <div className="page-header mt-5" style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

                    <Button
                        variant="outlined"
                        onClick={() => navigate("/notification-history")}
                        startIcon={<ArrowBackIcon />}
                        sx={{ borderRadius: "10px", textTransform: "none" }}
                    >
                        Back
                    </Button>
                </div>
                <div style={{ flex: "1 1 auto" }}>
                    <h3 className="page-title" style={{ margin: 0 }}>
                        {notification?.title || "Notification Details"}
                    </h3>
                </div>
                <IconButton
                    aria-label="close"
                    onClick={() => navigate("/notification-history")}
                    size="small"
                    sx={{ alignSelf: "center" }}
                >
                    <CloseIcon />
                </IconButton>
            </div>

            {!loading && notification ? (
                <Box sx={{ mt: 3 }}>
                    <StatsBar />
                </Box>
            ) : null}

            <Box className="notification-detail-card">
                {loading ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading details...
                    </Typography>
                ) : notification ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 3,
                            alignItems: "stretch",
                        }}
                    >

                        {/* Left - Image */}
                        <Box
                            sx={{
                                flexBasis: { xs: "100%", md: "42%" },
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                            }}
                        >
                            {notification.image_url ? (
                                <Box
                                    sx={{
                                        width: "100%",
                                        borderRadius: 3,
                                        overflow: "hidden",
                                        boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <img
                                        src={notification.image_url}
                                        alt={notification.title}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: 220,
                                        borderRadius: 3,
                                        border: "1px dashed #ccc",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "text.secondary",
                                        fontSize: 14,
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    No image available
                                </Box>
                            )}
                        </Box>

                        {/* Right - Details */}
                        <Box
                            sx={{
                                flexBasis: { xs: "100%", md: "58%" },
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.5,
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{ letterSpacing: 1, color: "text.secondary" }}
                                >
                                    Title
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {notification.title || "-"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{ letterSpacing: 1, color: "text.secondary" }}
                                >
                                    Message
                                </Typography>
                                <Typography variant="body1">{notification.body || "-"}</Typography>
                            </Box>

                            {/* Country moved below to avoid wrapping crowd */}
                            <Box mt={1}>
                                <Typography
                                    variant="overline"
                                    sx={{ letterSpacing: 1, color: "text.secondary" }}
                                >
                                    Country
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    mt={0.5}
                                    flexWrap="wrap"
                                >
                                    {notification?.countryCode?.length ? (
                                        notification.countryCode.map((code, index) => (
                                            <Chip
                                                key={index}
                                                label={code}
                                                size="small"
                                                sx={{
                                                    borderRadius: "8px",
                                                    fontWeight: 500,
                                                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                                                }}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2">-</Typography>
                                    )}
                                </Stack>
                            </Box>
                            <Divider />

                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{ letterSpacing: 1, color: "text.secondary" }}
                                >
                                    Link
                                </Typography>
                                <Box mt={0.5}>
                                    {notification.link ? (
                                        <a
                                            href={notification.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ color: "primary.main", wordBreak: "break-all" }}
                                            >
                                                {notification.link}
                                            </Typography>
                                        </a>
                                    ) : (
                                        <Typography variant="body1">-</Typography>
                                    )}
                                </Box>
                            </Box>

                            {!!usableButtons.length && (
                                <Box>
                                    <Typography
                                        variant="overline"
                                        sx={{ letterSpacing: 1, color: "text.secondary" }}
                                    >
                                        Buttons
                                    </Typography>
                                    <Stack spacing={1.25} mt={1}>
                                        {usableButtons.map((btn, idx) => {
                                            const label = btn?.label || `Button ${idx + 1}`;
                                            const link = btn?.link;
                                            const code = btn?.discountCode;
                                            return (
                                                <Box
                                                    key={btn?.id || `${label}-${idx}`}
                                                    sx={{
                                                        borderRadius: 2,
                                                        border: "1px solid rgba(0,0,0,0.08)",
                                                        backgroundColor: "#fff",
                                                        px: 1.5,
                                                        py: 1.25,
                                                    }}
                                                >
                                                    <Stack
                                                        direction={{ xs: "column", sm: "row" }}
                                                        spacing={1}
                                                        alignItems={{ xs: "flex-start", sm: "center" }}
                                                        justifyContent="space-between"
                                                    >
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: 14,
                                                                    lineHeight: 1.2,
                                                                }}
                                                            >
                                                                {label}
                                                            </Typography>
                                                            {link ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: "text.secondary",
                                                                        wordBreak: "break-all",
                                                                        mt: 0.25,
                                                                    }}
                                                                >
                                                                    {link}
                                                                </Typography>
                                                            ) : code ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: "text.secondary", mt: 0.25 }}
                                                                >
                                                                    Discount code
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: "text.secondary", mt: 0.25 }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            flexWrap="wrap"
                                                            useFlexGap
                                                            sx={{ justifyContent: "flex-end" }}
                                                        >
                                                            {code ? (
                                                                <>
                                                                    <Chip
                                                                        label={code}
                                                                        variant="outlined"
                                                                        sx={{ fontWeight: 700 }}
                                                                    />
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => handleCopyCode(code)}
                                                                    >
                                                                        {copiedCode === code ? "Copied" : "Copy"}
                                                                    </Button>
                                                                </>
                                                            ) : null}
                                                            {link ? (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    component="a"
                                                                    href={link}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    sx={{
                                                                        color: "#fff",
                                                                        textDecoration: "none",
                                                                        "&:hover": {
                                                                            backgroundColor: "primary.dark",
                                                                            color: "#fff",
                                                                            textDecoration: "none",
                                                                        },
                                                                        "&:visited": {
                                                                            color: "#fff",
                                                                        },
                                                                    }}
                                                                >
                                                                    Open
                                                                </Button>
                                                            ) : null}
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            )}

                            <Stack direction="row" spacing={4} flexWrap="wrap" rowGap={1.5}>
                                <Box>
                                    <Typography
                                        variant="overline"
                                        sx={{ letterSpacing: 1, color: "text.secondary" }}
                                    >
                                        Created At
                                    </Typography>
                                    <Typography variant="body2" mt={0.5}>
                                        {createdAtLabel}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No details available for this notification.
                    </Typography>
                )}
                {/* {!loading && notification ? (
                    <Box sx={{ mt: 3 }}>
                        <StatsBar />
                    </Box>
                ) : null} */}
            </Box>

            {notification?.customerIds?.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2 }}
                    >
                        Customer Details
                    </Typography>

                    <Box
                        sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            overflow: "hidden",
                            backgroundColor: "#fff",
                        }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead style={{ background: "#f9fafb" }}>
                                <tr>
                                    <th style={thStyle}>#</th>
                                    <th style={thStyle}>Email</th>
                                    <th style={thStyle}>Customer ID</th>
                                    <th style={thStyle}>Platform</th>
                                    <th style={thStyle}>Country</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginatedCustomers?.map((cust, index) => {
                                    return (
                                        <tr key={cust?._id || index}>
                                            <td style={tdStyle}>
                                                {(page - 1) * rowsPerPage + index + 1}
                                            </td>

                                            <td style={tdStyle}>
                                                {cust?.customerEmail || "-"}
                                            </td>

                                            <td style={tdStyle}>
                                                {cust?.customerId || "-"}
                                            </td>

                                            <td style={tdStyle}>
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        color:
                                                            cust?.platform === "IOS"
                                                                ? "#6366f1"
                                                                : "#16a34a",
                                                    }}
                                                >
                                                    {cust?.platform || "-"}
                                                </span>
                                            </td>

                                            <td style={tdStyle}>
                                                {cust?.countryCode || "-"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        {/* Left: Info */}
                        <Typography variant="body2" color="text.secondary">
                            Showing {(page - 1) * rowsPerPage + 1}–
                            {Math.min(page * rowsPerPage, total)} of {total}
                        </Typography>

                        {/* Right: Controls */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                            {/* Rows per page */}
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(1);
                                }}
                                style={{
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: "1px solid #e5e7eb",
                                }}
                            >
                                {[5, 10, 20].map((n) => (
                                    <option key={n} value={n}>
                                        {n} / page
                                    </option>
                                ))}
                            </select>

                            {/* Prev */}
                            <button
                                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                disabled={page === 1}
                                style={btnStyle(page === 1)}
                            >
                                Prev
                            </button>

                            {/* Page numbers */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                                {/* First + Dots */}
                                {page > 3 && (
                                    <>
                                        <button onClick={() => setPage(1)} style={btnStyle(false)}>1</button>
                                        <span>...</span>
                                    </>
                                )}

                                {/* Middle pages */}
                                {getVisiblePages().map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        style={{
                                            ...btnStyle(false),
                                            background: page === p ? "#4f46e5" : "#fff",
                                            color: page === p ? "#fff" : "#374151",
                                        }}
                                    >
                                        {p}
                                    </button>
                                ))}

                                {/* Last + Dots */}
                                {page < totalPages - 2 && (
                                    <>
                                        <span>...</span>
                                        <button onClick={() => setPage(totalPages)} style={btnStyle(false)}>
                                            {totalPages}
                                        </button>
                                    </>
                                )}

                            </Box>

                            {/* Next */}
                            <button
                                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                disabled={page === totalPages}
                                style={btnStyle(page === totalPages)}
                            >
                                Next
                            </button>

                        </Box>
                    </Box>
                </Box>
            )}
        </div>
    );
};
export default NotificationDetail;